import { List, Map } from 'immutable';
import { prop, union } from 'ramda';
import {
  SORT_CHANGED,
  CLEAR_VIDEOS,
  SEARCH_CHANGED,
  EDIT_VIDEO,
  MESSAGE,
  SHORTCODE_RECEIVED,
  POLLING_UPDATE_RECEIVED,
  UPLOAD_PROGRESS,
  UPLOAD,
  FETCH_VIDEO,
  RETRANSCODE,
} from 'src/actions';
import { MEDIA_STATUS } from 'src/common/types/media';
import { DASHBOARD_ASSIGN_LABELS_ACTION_TYPES } from 'src/dashboard/state/assign-labels/assign-labels-actions';
import { DASHBOARD_SIDEBAR_ACTION_TYPES } from 'src/dashboard/state/labels/labels-actions';
import { getVideosWithoutGroups } from 'src/dashboard/state/videos/videos-selectors';
import { getVideo } from 'src/misc/selectors';
import { PRIVACY_SETTING } from 'src/settings/constants';

import { VIDEO_PRIVACY_ACTION_TYPES } from '../video-privacy/video-privacy-actions';
import { VIDEOS_ACTION_TYPES } from './videos-actions';

function setVideo(
  videoCache,
  video,
  newKey = 'shortcode',
  oldKey = 'shortcode'
) {
  if (!(video instanceof Map)) {
    video = new Map(video);
  }

  const existingVideo = videoCache.get(video.get(oldKey));
  if (existingVideo) {
    // don't update thumbnail
    const currentThumbnail = existingVideo.get('thumbnail_url');
    const newThumbnail = video.get('thumbnail_url');
    if (
      currentThumbnail &&
      newThumbnail &&
      currentThumbnail.split('?')[0] === newThumbnail.split('?')[0]
    ) {
      video = video.delete('thumbnail_url');
    }

    return videoCache
      .delete(video.get(oldKey))
      .set(video.get(newKey), existingVideo.merge(video));
  }
  return replaceVideo(videoCache, video, newKey);
}

function setVideos(videoCache, videos, replace = false) {
  let output = videoCache;
  for (const video of videos) {
    if (replace) {
      output = replaceVideo(output, video);
    } else {
      output = setVideo(output, video);
    }
  }
  return output;
}

function syncVideos(videoCache, videos) {
  const shortcodes = videos.map(prop('shortcode'));
  const fetchedStatuses = union(
    [MEDIA_STATUS.READY, MEDIA_STATUS.PROCESSING],
    videos.map(prop('status'))
  );

  let cleanCache = new Map();
  cleanCache = setVideos(cleanCache, videos);
  videoCache.forEach((video, key) => {
    if (!fetchedStatuses.includes(video.get('status'))) {
      cleanCache = cleanCache.set(key, new Map(video.toObject()));
      if (video.get('shortcode')) {
        shortcodes.push(video.get('shortcode'));
      }
    }
  });
  return { cache: cleanCache, shortcodes: new List(shortcodes) };
}

function replaceVideo(videoCache, video, key = 'shortcode') {
  if (!(video instanceof Map)) {
    video = Map(video);
  }
  return videoCache.set(video.get(key), video);
}

const initialState = {
  isError: false,
  isLoading: true,
  shortcodes: new List(),
  total: 0,
  sort: 'date_added',
  sortd: 'DESC',
  count: 16,
  page: 1,
  search: null,
  group_id: null,
  cache: new Map(),
  uploads: new List(),
  pending: new List(),
  loaded: false,
};

export const videosReducer = (state = initialState, action) => {
  const { type, payload, error } = action;

  switch (type) {
    case POLLING_UPDATE_RECEIVED:
      return {
        ...state,
        cache: setVideos(
          state.cache,
          payload.map((item) =>
            new Map(item).delete('title').delete('labels').toObject()
          )
        ),
      };

    case VIDEOS_ACTION_TYPES.upload.createVideo:
      if (payload.local_id) {
        return {
          ...state,
          total: state.total + 1,
          pending: state.pending.unshift(payload.local_id),
          cache: replaceVideo(
            state.cache,
            new Map(payload)
              .set('status', payload.status || 0)
              .set('date_added', Math.floor(Date.now() / 1000))
              .set('new_upload', true),
            'local_id'
          ),
        };
      } else {
        return {
          ...state,
          uploads: state.uploads.unshift(payload.shortcode),
          shortcodes: state.shortcodes.unshift(payload.shortcode),
          cache: setVideo(
            state.cache,
            new Map(payload)
              .set('status', payload.status || 0)
              .set('new_upload', true)
          ),
        };
      }

    case SHORTCODE_RECEIVED:
      return {
        ...state,
        uploads: state.uploads.unshift(payload.shortcode),
        shortcodes: state.shortcodes.unshift(payload.shortcode),
        pending: state.pending.delete(state.pending.indexOf(payload.local_id)),
        cache: setVideo(
          state.cache,
          {
            ...new Map(payload.video).delete('title').toObject(),
            upload_metadata: payload,
            shortcode: payload.shortcode,
            local_id: payload.local_id,
          },
          'shortcode',
          'local_id'
        ),
      };

    case CLEAR_VIDEOS:
      return {
        ...state,
        shortcodes: state.shortcodes.clear(),
        loaded: false,
        total: 0,
        sort: 'date_added',
        sortd: 'DESC',
        count: 16,
        page: 1,
        search: null,
        group_id: null,
      };

    case VIDEOS_ACTION_TYPES.dashboard.fetchVideos.initiated:
      return {
        ...state,
        ...payload,
        isError: false,
        isLoading: true,
      };

    case VIDEOS_ACTION_TYPES.dashboard.fetchVideos.fulfilled: {
      const { total, videos } = payload;
      const newState = syncVideos(state.cache, videos);

      return {
        ...state,
        ...newState,
        isLoading: false,
        loaded: true,
        total,
      };
    }

    case VIDEOS_ACTION_TYPES.dashboard.fetchVideos.failed:
      return {
        ...state,
        isError: true,
        isLoading: false,
      };

    case SORT_CHANGED:
      return { ...state, sort: payload };

    case SEARCH_CHANGED:
      return {
        ...state,
        search: payload === '' ? null : payload,
      };

    case RETRANSCODE:
      return {
        ...state,
        cache: setVideo(state.cache, new Map(payload).set('files', null)),
      };

    case UPLOAD_PROGRESS:
    case FETCH_VIDEO:
    case UPLOAD:
      if (!error && payload.shortcode) {
        return {
          ...state,
          cache: setVideo(state.cache, payload),
        };
      }
      return state;

    case EDIT_VIDEO:
      return {
        ...state,
        cache: payload.shortcode
          ? setVideo(state.cache, payload)
          : setVideo(state.cache, payload, 'local_id', 'local_id'),
      };

    case VIDEOS_ACTION_TYPES.deleteVideo:
      let { uploads, pending, shortcodes, cache } = state;
      if (payload.local_id) {
        const uploadIdx = uploads.indexOf(payload.local_id);
        const pendingIdx = pending.indexOf(payload.local_id);
        uploads = uploadIdx >= 0 ? uploads.delete(uploadIdx) : uploads;
        pending = pendingIdx >= 0 ? pending.delete(pendingIdx) : pending;
        cache = cache.delete(payload.local_id);
      }
      if (payload.shortcode) {
        const uploadIdx = uploads.indexOf(payload.shortcode);
        const shortcodeIdx = shortcodes.indexOf(payload.shortcode);
        uploads = uploadIdx >= 0 ? uploads.delete(uploadIdx) : uploads;
        shortcodes =
          shortcodeIdx >= 0 ? shortcodes.delete(shortcodeIdx) : shortcodes;
        cache = cache.delete(payload.shortcode);
      }
      return { ...state, pending, uploads, shortcodes, cache };

    case MESSAGE:
      if (payload.type === 'video') {
        // ignore title changes so we don't overwrite
        delete payload.payload.title;
        // ignore labels changes so we don't overwrite
        delete payload.payload.labels;
        //only do current version
        const video = getVideo(state.cache, payload.payload.shortcode);
        if (video && video.max_version == payload.payload.version) {
          return {
            ...state,
            cache: setVideo(state.cache, payload.payload),
          };
        }
      }

      return state;

    case DASHBOARD_SIDEBAR_ACTION_TYPES.deleteLabelModal.saveLabelFulfilled: {
      const videos = getVideosWithoutGroups({ videos: state });

      const updatedVideos = videos.map((video) => ({
        ...video,
        labels: video.labels.filter((label) => label.id !== payload),
      }));

      return {
        ...state,
        cache: setVideos(state.cache, updatedVideos),
      };
    }

    case DASHBOARD_ASSIGN_LABELS_ACTION_TYPES.assignLabelsModal
      .saveAssignmentsFulfilled: {
      const { shortcode, labelIdsToAdd, labelIdsToRemove } = payload;

      const video = getVideo(state.cache, shortcode);
      if (video) {
        const updatedLabels = video.labels.filter(
          (label) => !labelIdsToRemove.includes(label.id)
        );
        updatedLabels.push(
          ...labelIdsToAdd.map((labelId) => ({ id: labelId }))
        );

        const updatedVideo = {
          ...video,
          labels: updatedLabels,
        };

        return {
          ...state,
          cache: setVideo(state.cache, updatedVideo),
        };
      }

      return state;
    }

    case DASHBOARD_ASSIGN_LABELS_ACTION_TYPES.dragAndDropAssign.saveFulfilled: {
      const { shortcodes, labelId } = payload;

      const cache = shortcodes.reduce((result, shortcode) => {
        const video = getVideo(result, shortcode);

        if (!video || video.labels.some((label) => label.id === labelId)) {
          return result;
        }

        return setVideo(result, {
          ...video,
          labels: [...video.labels, { id: labelId }],
        });
      }, state.cache);

      return {
        ...state,
        cache,
      };
    }

    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.privacySection.visibility
      .changed:
    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.privacySection.visibility
      .errored: {
      const { shortcode, visibility } = payload;
      const video = getVideo(state.cache, shortcode);

      if (!video) {
        return state;
      }

      return {
        ...state,
        cache: setVideo(state.cache, {
          ...video,
          privacy_settings: {
            ...video.privacy_settings,
            visibility: visibility,
          },
        }),
      };
    }

    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.restrictionSection
      .domainRestrictions.toggled:
    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.restrictionSection
      .domainRestrictions.errored: {
      const video = getVideo(state.cache, payload);

      if (!video) {
        return state;
      }

      return {
        ...state,
        cache: setVideo(state.cache, {
          ...video,
          privacy_settings: {
            ...video.privacy_settings,
            domain_restrictions:
              video.privacy_settings?.domain_restrictions === 'allowlist'
                ? 'off'
                : 'allowlist',
          },
        }),
      };
    }

    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.restrictionSection
      .allowedDomainInput.changed: {
      const { shortcode, allowedDomain } = payload;
      const video = getVideo(state.cache, shortcode);

      if (!video) {
        return state;
      }

      return {
        ...state,
        cache: setVideo(state.cache, {
          ...video,
          privacy_settings: {
            ...video.privacy_settings,
            allowed_domain: allowedDomain,
          },
        }),
      };
    }

    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.restrictionSection
      .passwordProtection.turnedOn: {
      const shortcode = payload;
      const video = getVideo(state.cache, shortcode);

      if (!video) {
        return state;
      }

      return {
        ...state,
        cache: setVideo(state.cache, {
          ...video,
          privacy_settings: {
            ...video.privacy_settings,
            password_protected: true,
          },
        }),
      };
    }

    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.restrictionSection
      .passwordProtection.turnedOff: {
      const shortcode = payload;
      const video = getVideo(state.cache, shortcode);

      if (!video) {
        return state;
      }

      return {
        ...state,
        cache: setVideo(state.cache, {
          ...video,
          privacy: PRIVACY_SETTING.BY_LINK,
          privacy_settings: {
            ...video.privacy_settings,
            password_protected: false,
          },
        }),
      };
    }

    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.playerPreferencesSection
      .allowDownload.toggled:
    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.playerPreferencesSection
      .allowDownload.errored: {
      const video = getVideo(state.cache, payload);

      if (!video) {
        return state;
      }

      return {
        ...state,
        cache: setVideo(state.cache, {
          ...video,
          privacy_settings: {
            ...video.privacy_settings,
            allow_download: !video.privacy_settings?.allow_download,
          },
        }),
      };
    }

    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.playerPreferencesSection
      .allowSharing.toggled:
    case VIDEO_PRIVACY_ACTION_TYPES.videoPrivacyModal.playerPreferencesSection
      .allowSharing.errored: {
      const video = getVideo(state.cache, payload);

      if (!video) {
        return state;
      }

      return {
        ...state,
        cache: setVideo(state.cache, {
          ...video,
          privacy_settings: {
            ...video.privacy_settings,
            allow_sharing: !video.privacy_settings?.allow_sharing,
          },
        }),
      };
    }

    default:
      return state;
  }
};
